import {
  NewsletterVariant,
  NewsletterWidget,
  ResetAfterSuccessTimeout,
  theme,
  useResponsive,
} from "@product/scmp-sdk";
import type { NewsletterItem } from "@product/scmp-sdk/dist/types/components/newsletter/types";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useEffect, useMemo, useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { AppFooter } from "~/components/app-footer";
import { FallbackLoading } from "~/components/fallback-loading";
import { PageSeo } from "~/components/meta";
import { useKeywordMetaTags } from "~/components/meta/hooks";
import { NewsletterCategoryItemList } from "~/components/newsletter/newsletter-list/newsletter-category-item-list";
import { NewsletterListContext } from "~/components/newsletter/newsletter-list/newsletter-category-item-list/contexts";
import { useNewsletterWidget } from "~/components/newsletter/newsletter-widget-container/hooks";
import { trackingAtom } from "~/components/tracking";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import type { newslettersPageQuery } from "~/queries/__generated__/newslettersPageQuery.graphql";

import { useNewsletterScrollEffect, usePreselectNewsletter } from "./hooks";
import {
  Category,
  CategoryItemListContainer,
  CategoryItemListWrapper,
  CategoryListContainer,
  Container,
  Heading,
  NewsletterWidgetContainer,
  PreviewContainer,
  StyledMobileNewsletterPreview,
  StyledNewsletterPreview,
  StyledSlide,
} from "./styles";
import type { NewsletterType } from "./types";

const query = graphql`
  query newslettersPageQuery {
    newsletterCategories(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
      ...hooksNewsletterScrollEffectNewsletterCategoryConnection
      ...hooksPreselectNewsletterNewsletterCategoryConnection
      edges {
        node {
          entityId
          name
          ...newsletterCategoryItemListNewsletterCategory
          newsletters(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
            edges {
              node {
                entityId
                name
                sourceUrl
              }
            }
          }
        }
      }
    }
  }
`;

const NewslettersPage: RelayComponent<unknown, newslettersPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const [activeNewsletterId, setActiveNewsletterId] = useState(
    first(first(data.newsletterCategories?.edges)?.node.newsletters?.edges)?.node?.entityId ?? "",
  );
  const [checkedNewsletterIdsMap, setCheckedNewsletterIdsMap] = useState<Record<string, string[]>>(
    {},
  );

  const newsletterMap = useMemo(
    () =>
      (
        data.newsletterCategories?.edges?.flatMap(edge => edge.node.newsletters?.edges) ?? []
      ).reduce((accumulator, edge) => {
        if (!edge?.node?.entityId) return accumulator;
        accumulator[edge?.node?.entityId] = edge.node;
        return accumulator;
      }, {} as Record<string, NewsletterType>),
    [data.newsletterCategories?.edges],
  );

  const activeNewsletter = useMemo(
    () => newsletterMap[activeNewsletterId ?? ""],
    [activeNewsletterId, newsletterMap],
  );

  const sourceUrl = useMemo(() => activeNewsletter?.sourceUrl ?? "", [activeNewsletter]);

  const {
    activeNewsletterCategoryId,
    handleCategoryClick,
    handleCategoryItemListIntersection,
    isStopIntersectionObserve,
    newsletterCategoryItemListReferences,
  } = useNewsletterScrollEffect(data.newsletterCategories);

  const [isShowNewsletterWidget, setIsShowNewsletterWidget] = useState(false);
  const checkedNewsletter = useMemo<NewsletterItem[]>(
    () =>
      Object.values(checkedNewsletterIdsMap)
        .flat()
        .map(newsletterId => {
          const newsletter = newsletterMap[newsletterId];
          return {
            entityId: newsletter.entityId,
            name: newsletter.name,
          };
        }),
    [checkedNewsletterIdsMap, newsletterMap],
  );
  useEffect(() => {
    setIsShowNewsletterWidget(checkedNewsletter.length > 0);
  }, [checkedNewsletter]);

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);

  const keywordTags = useKeywordMetaTags("newsletters");
  const [isDisplayMobilePreview, setIsDisplayMobilePreview] = useState(false);

  const { dataMatrix } = useNewsletterWidget({
    contentType: "widget",
    eventCategory: "Newsletters (Index)",
    section: "",
  });
  usePreselectNewsletter(
    data.newsletterCategories,
    setCheckedNewsletterIdsMap,
    setActiveNewsletterId,
  );

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  return (
    <>
      <PageSeo
        additionalMetaTags={[...keywordTags]}
        openGraph={{ title: "Newsletters | South China Morning Post" }}
        title="Newsletters"
      />
      <Container>
        <Heading>Sign up for our Newsletters</Heading>
        <CategoryListContainer $stickyStackedHeight={appBarStickyHeight}>
          {data?.newsletterCategories?.edges.map((edge, index) => (
            <Category
              $isFocused={edge.node.entityId === activeNewsletterCategoryId}
              key={index}
              onClick={() => handleCategoryClick(edge.node.entityId)}
            >
              {edge.node.name}
            </Category>
          ))}
        </CategoryListContainer>
        <NewsletterListContext.Provider
          value={{
            activeNewsletterId,
            checkedNewsletterIdsMap,
            setActiveNewsletterId,
            setCheckedNewsletterIdsMap,
            setIsDisplayMobilePreview,
          }}
        >
          <CategoryItemListContainer>
            <CategoryItemListWrapper>
              {data?.newsletterCategories?.edges.map((edge, index) => (
                <NewsletterCategoryItemList
                  isStopIntersectionObserve={isStopIntersectionObserve}
                  key={index}
                  ref={element => {
                    if (!element) return;
                    newsletterCategoryItemListReferences.current[edge.node.entityId] = element;
                  }}
                  reference={edge.node}
                  onIntersection={handleCategoryItemListIntersection}
                />
              ))}
            </CategoryItemListWrapper>
          </CategoryItemListContainer>
        </NewsletterListContext.Provider>
        <PreviewContainer $stickyStackedHeight={appBarStickyHeight}>
          <StyledNewsletterPreview sourceUrl={sourceUrl} />
        </PreviewContainer>
        <StyledMobileNewsletterPreview
          isOpen={isDisplayMobilePreview}
          newsletterTitle={activeNewsletter?.name}
          sourceUrl={sourceUrl}
          onClose={() => setIsDisplayMobilePreview(false)}
        />
      </Container>
      <StyledSlide
        direction="up"
        in={isShowNewsletterWidget}
        timeout={500}
        mountOnEnter
        unmountOnExit
      >
        <NewsletterWidgetContainer>
          <NewsletterWidget
            dataMatrix={dataMatrix}
            multipleNewsletters={true}
            newsletterItems={checkedNewsletter}
            titleComponent={
              isTabletUp
                ? `Sign up for our free newsletters and get our award\u2011winning work delivered directly to your inbox.`
                : ""
            }
            variant={NewsletterVariant.NewsletterList}
            onSuccess={() =>
              // Uncheck all newsletter after success
              setTimeout(() => setCheckedNewsletterIdsMap({}), ResetAfterSuccessTimeout)
            }
          />
        </NewsletterWidgetContainer>
      </StyledSlide>
      <AppFooter />
    </>
  );
};

NewslettersPage.displayName = "NewslettersPage";

export default withRelay(NewslettersPage, query, {
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
});
